import React, { useState } from 'react';
import { Wallet } from '../../types/wallet';
import { reportPayment } from '../../services/wallet.service';
import { Spinner } from '../common/Spinner';
import { toast } from 'react-hot-toast';

interface WalletCardProps {
  wallet: Wallet;
  onUpdate?: () => void;
}

export const WalletCard: React.FC<WalletCardProps> = ({ wallet, onUpdate }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<'upi' | 'cash'>('upi');
  const [refId, setRefId] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRecharge = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) {
        return toast.error("Enter valid positive amount");
    }
    
    setLoading(true);
    try {
      await reportPayment(
        wallet.id, 
        Number(amount), 
        method, 
        refId || (method === 'cash' ? 'CASH-HANDOVER' : '')
      );
      toast.success("Payment reported. Your balance updates after admin verification.");
      setIsAdding(false);
      setAmount('');
      setRefId('');
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error(err);
      toast.error("Failed to update wallet");
    } finally {
      setLoading(false);
    }
  };

  const isNegative = wallet.balance < 0;

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
      {/* HEADER */}
      <div className={`p-6 ${isNegative ? 'bg-red-50' : 'bg-green-50'} flex justify-between items-center transition-colors`}>
        <div>
          <p className="text-sm font-bold text-gray-500 uppercase tracking-wider">
            {isNegative ? "Payment Due" : "Available Credits"}
          </p>
          <h2 className={`text-3xl font-extrabold ${isNegative ? 'text-red-600' : 'text-green-600'}`}>
            ₹ {Math.abs(wallet.balance).toFixed(2)}
          </h2>
          {isNegative && (
             <p className="text-xs text-red-500 mt-1 font-medium">Please clear your dues to place new orders.</p>
          )}
        </div>
        <button 
          onClick={() => setIsAdding(!isAdding)}
          className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-bold shadow hover:bg-black transition-colors"
        >
          {isAdding ? "Cancel" : "+ Add Money"}
        </button>
      </div>

      {/* RECHARGE FORM */}
      {isAdding && (
        <div className="p-4 bg-gray-50 border-t border-gray-100 animate-in slide-in-from-top-2">
          <form onSubmit={handleRecharge} className="space-y-4">
             <div>
               <label className="block text-xs font-bold text-gray-700 mb-1">Amount to Add (₹)</label>
               <input 
                 type="number" 
                 className="w-full p-2 border rounded font-mono text-lg"
                 placeholder="e.g. 500"
                 value={amount}
                 onChange={e => setAmount(e.target.value)}
                 required
                 min="1"
               />
             </div>
             
             <div className="grid grid-cols-2 gap-4">
               <label className={`cursor-pointer border-2 p-3 rounded-lg text-center text-sm font-bold transition-all ${method === 'upi' ? 'bg-blue-50 border-blue-500 text-blue-700' : 'bg-white border-transparent hover:bg-gray-100'}`}>
                  <input type="radio" name="method" className="hidden" onClick={() => setMethod('upi')} checked={method === 'upi'} />
                  📲 UPI / GPay
               </label>
               <label className={`cursor-pointer border-2 p-3 rounded-lg text-center text-sm font-bold transition-all ${method === 'cash' ? 'bg-green-50 border-green-500 text-green-700' : 'bg-white border-transparent hover:bg-gray-100'}`}>
                  <input type="radio" name="method" className="hidden" onClick={() => setMethod('cash')} checked={method === 'cash'} />
                  💵 Cash
               </label>
             </div>

             <div>
               <label className="block text-xs font-bold text-gray-700 mb-1">
                 {method === 'upi' ? "UPI Reference / UTR Number" : "Handover Notes (Optional)"}
               </label>
               <input 
                 type="text" 
                 className="w-full p-2 border rounded"
                 placeholder={method === 'upi' ? "Enter 12-digit UTR" : "Given to delivery agent..."}
                 value={refId}
                 onChange={e => setRefId(e.target.value)}
                 required={method === 'upi'}
               />
               {method === 'upi' && <p className="text-[10px] text-gray-400 mt-1">
                  Send payment to <strong>vyomaser@axisb</strong>
               </p>}
             </div>

             <button disabled={loading} className="w-full bg-blue-600 text-white py-3 rounded-lg font-bold text-sm hover:bg-blue-700 disabled:opacity-50 transition-colors shadow-md shadow-blue-200">
               {loading ? <Spinner size={16} color="white" /> : `Confirm & Update Balance`}
             </button>
             
             <div className="flex items-start gap-2 bg-yellow-50 p-2 rounded text-[10px] text-yellow-800 border border-yellow-100">
               <span>ℹ️</span>
               <p>Your balance will update immediately. Our team will verify the payment within 24 hours. Incorrect reports will reverse the balance.</p>
             </div>
          </form>
        </div>
      )}
    </div>
  );
};